import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api.js';
import { queryKeys } from '../../lib/queryKeys.js';
import { cn } from '../../lib/format.js';
import { StackedLogo } from './Wordmark.jsx';
import { Spinner } from '../ui/Spinner.jsx';
import { toast } from '../../stores/ui.js';

/**
 * The footer carries the full sitemap (PRD §6.2, item 12). The legal column is
 * built from the CMS rather than hardcoded, so a page an operator adds appears
 * here without a deploy — and a legal page a payment gateway checks for cannot
 * be quietly orphaned.
 *
 * It is the black of the masthead, so the page is bracketed by the same band
 * top and bottom, with gold kept for the headings and the one button.
 */

/** Glyphs for the networks the admin panel offers. Anything else falls back to its name. */
const SOCIAL_ICONS = {
  instagram: {
    label: 'Instagram',
    icon: (
      <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  facebook: {
    label: 'Facebook',
    icon: (
      <svg viewBox="0 0 24 24" className="size-4" fill="currentColor" aria-hidden="true">
        <path d="M13.5 21v-7.5H16l.4-3h-2.9V8.6c0-.87.25-1.46 1.5-1.46h1.55V4.45c-.27-.04-1.19-.12-2.26-.12-2.24 0-3.77 1.37-3.77 3.87v2.3H8v3h2.52V21z" />
      </svg>
    ),
  },
  x: {
    label: 'X',
    icon: (
      <svg viewBox="0 0 24 24" className="size-3.5" fill="currentColor" aria-hidden="true">
        <path d="M17.75 3h3.07l-6.7 7.66L22 21h-6.17l-4.83-6.32L5.47 21H2.4l7.17-8.2L2 3h6.33l4.37 5.77zm-1.08 16.2h1.7L7.4 4.73H5.58z" />
      </svg>
    ),
  },
  youtube: {
    label: 'YouTube',
    icon: (
      <svg viewBox="0 0 24 24" className="size-4" fill="currentColor" aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M21.6 7.2a2.5 2.5 0 0 0-1.76-1.77C18.25 5 12 5 12 5s-6.25 0-7.84.43A2.5 2.5 0 0 0 2.4 7.2 26 26 0 0 0 2 12a26 26 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.76 1.77C5.75 19 12 19 12 19s6.25 0 7.84-.43a2.5 2.5 0 0 0 1.76-1.77A26 26 0 0 0 22 12a26 26 0 0 0-.4-4.8M10 15V9l5.2 3z"
        />
      </svg>
    ),
  },
  pinterest: {
    label: 'Pinterest',
    icon: (
      <svg viewBox="0 0 24 24" className="size-4" fill="currentColor" aria-hidden="true">
        <path d="M12 2a10 10 0 0 0-3.64 19.31c-.09-.79-.17-2 .03-2.86l1.17-4.97s-.3-.6-.3-1.48c0-1.39.8-2.42 1.81-2.42.85 0 1.26.64 1.26 1.4 0 .86-.55 2.14-.83 3.32-.24 1 .5 1.81 1.48 1.81 1.77 0 3.14-1.87 3.14-4.57 0-2.39-1.72-4.06-4.17-4.06-2.84 0-4.5 2.13-4.5 4.33 0 .86.33 1.78.74 2.28a.3.3 0 0 1 .07.29l-.28 1.13c-.04.18-.15.22-.33.13-1.25-.58-2.03-2.4-2.03-3.87 0-3.15 2.29-6.04 6.6-6.04 3.46 0 6.16 2.47 6.16 5.77 0 3.44-2.17 6.22-5.18 6.22-1.01 0-1.97-.53-2.29-1.15l-.62 2.38c-.23.87-.84 1.96-1.25 2.62A10 10 0 1 0 12 2" />
      </svg>
    ),
  },
};

const footerLink =
  'text-xs text-white/70 transition-colors hover:text-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const emailId = useId();

  const { data: groups } = useQuery({
    queryKey: queryKeys.pages.footer(),
    queryFn: () => api.get('/pages/footer'),
    staleTime: 15 * 60 * 1000,
  });

  const { data: settings } = useQuery({
    queryKey: queryKeys.settings.public(),
    queryFn: () => api.get('/settings/public'),
    staleTime: 15 * 60 * 1000,
  });

  const subscribe = useMutation({
    mutationFn: (address) => api.post('/newsletter', { email: address }),
    onSuccess: (result) => {
      setEmail('');
      setError('');
      toast({
        title: result.alreadySubscribed ? 'You are already on the list.' : 'You are on the list.',
        description: result.alreadySubscribed
          ? 'Nothing to do — we have this address already.'
          : 'Expect a note when something worth reading arrives.',
        tone: 'success',
      });
    },
    onError: (failure) => {
      setError(
        failure instanceof ApiError
          ? (failure.fields?.email ?? failure.message)
          : 'That did not go through. Try again in a moment.',
      );
    },
  });

  const shop = [
    { label: 'Everything', to: '/shop' },
    { label: 'Fragrance', to: '/shop?world=fragrance' },
    { label: 'Sport', to: '/shop?world=sport' },
    { label: 'Brands', to: '/brands' },
    { label: 'New arrivals', to: '/shop?isNewArrival=true' },
  ];

  const help = [
    { label: 'Track an order', to: '/orders/track' },
    { label: 'Contact us', to: '/contact' },
    { label: 'FAQ', to: '/faq' },
    { label: 'Store locator', to: '/store-locator' },
  ];

  const about = [
    { label: 'About us', to: '/about' },
    { label: 'Blogs', to: '/blogs' },
  ];

  const legal = (groups ?? []).find((group) => group.group === 'Legal')?.items ?? [];

  const socials = Object.entries(settings?.social ?? {}).filter(([, url]) => Boolean(url));

  const address = settings?.contact?.address;

  return (
    <footer className="mt-section-lg bg-noir text-white/70">
      <div className="border-b border-white/10">
        <section className="mx-auto grid max-w-(--container-content) gap-8 px-4 py-12 sm:px-6 lg:grid-cols-[1fr_auto] lg:items-center lg:gap-16">
          <div>
            <p className="text-2xs font-semibold tracking-[0.12em] text-brand uppercase">Newsletter</p>
            <h2 className="mt-2 font-display text-xl text-white">
              Attars and perfumes, and the notes behind each one.
            </h2>
            <p className="mt-2 max-w-md text-xs leading-relaxed text-white/60">
              One note a month about what we have stocked and why. No countdowns, no manufactured
              urgency. Unsubscribe in one click.
            </p>
          </div>

          <form
            className="w-full lg:w-[26rem]"
            onSubmit={(event) => {
              event.preventDefault();
              if (!email.trim()) {
                setError('Enter an email address.');
                return;
              }
              subscribe.mutate(email.trim());
            }}
            noValidate
          >
            <label htmlFor={emailId} className="sr-only">
              Email address
            </label>
            <div className="flex">
              <input
                id={emailId}
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setError('');
                }}
                placeholder="you@example.com"
                autoComplete="email"
                aria-invalid={error ? 'true' : undefined}
                aria-describedby={error ? `${emailId}-error` : undefined}
                className={cn(
                  'h-12 min-w-0 flex-1 border border-r-0 bg-white/5 px-4 text-sm text-white placeholder:text-white/40 focus:bg-white/10 focus:outline-none',
                  error ? 'border-red-300' : 'border-white/20 focus:border-brand',
                )}
              />
              <button
                type="submit"
                disabled={subscribe.isPending}
                className="flex h-12 shrink-0 items-center gap-2 bg-brand px-5 text-2xs font-semibold tracking-[0.08em] text-white uppercase transition-colors hover:bg-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-wait disabled:opacity-80 sm:px-7"
              >
                {subscribe.isPending ? (
                  <>
                    <Spinner className="size-3.5" label="Adding" />
                    <span aria-hidden="true">Adding</span>
                  </>
                ) : (
                  'Subscribe'
                )}
              </button>
            </div>
            {error && (
              <p id={`${emailId}-error`} role="alert" className="mt-2 text-2xs text-red-300">
                {error}
              </p>
            )}
          </form>
        </section>
      </div>

      <div className="mx-auto max-w-(--container-content) px-4 sm:px-6">
        <nav
          aria-label="Footer"
          className="grid grid-cols-2 gap-x-6 gap-y-10 py-12 sm:grid-cols-3 lg:grid-cols-[1.5fr_repeat(5,1fr)] lg:gap-8"
        >
          <div className="col-span-2 sm:col-span-3 lg:col-span-1">
            <Link
              to="/"
              aria-label={`${settings?.store?.name ?? 'Golden Era'}, home`}
              className="inline-flex focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand"
            >
              <StackedLogo className="h-20" />
            </Link>
            {settings?.store?.tagline && (
              <p className="mt-4 max-w-xs text-xs leading-relaxed text-white/60">{settings.store.tagline}</p>
            )}

            {socials.length > 0 && (
              <ul className="mt-5 flex flex-wrap gap-2.5">
                {socials.map(([name, url]) => {
                  const known = SOCIAL_ICONS[name];

                  return (
                    <li key={name}>
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer noopener"
                        aria-label={known ? known.label : undefined}
                        className={cn(
                          'grid h-9 place-items-center rounded-full border border-white/20 text-white/80 transition-colors hover:border-brand hover:bg-brand hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
                          known ? 'w-9' : 'px-3.5 text-2xs tracking-[0.08em] uppercase',
                        )}
                      >
                        {known ? known.icon : name}
                      </a>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <FooterColumn title="Shop" links={shop} />
          <FooterColumn title="Help" links={help} />
          <FooterColumn title="Company" links={about} />
          <FooterColumn
            title="Legal"
            links={legal.map((page) => ({ label: page.title, to: `/${page.slug}` }))}
          />

          <div className="col-span-2 sm:col-span-1">
            <ColumnTitle>Visit</ColumnTitle>
            <address className="mt-4 space-y-1 text-xs leading-relaxed text-white/70 not-italic">
              <p className="text-white">{address?.line1}</p>
              {address?.line2 && <p>{address.line2}</p>}
              <p>
                {address?.city} {address?.pincode}
              </p>
              {settings?.contact?.phone && (
                <p className="pt-2">
                  <a href={`tel:${settings.contact.phone.replace(/\s/g, '')}`} className={footerLink}>
                    {settings.contact.phone}
                  </a>
                </p>
              )}
              {settings?.contact?.email && (
                <p>
                  <a href={`mailto:${settings.contact.email}`} className={cn(footerLink, 'break-all')}>
                    {settings.contact.email}
                  </a>
                </p>
              )}
            </address>
          </div>
        </nav>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-(--container-content) flex-col gap-2 px-4 py-6 text-2xs text-white/50 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p>
            © {new Date().getFullYear()} {settings?.store?.name ?? 'Golden Era'}. All rights reserved.
          </p>
          <p>Prices include GST · Made in India</p>
        </div>
      </div>
    </footer>
  );
};

const ColumnTitle = ({ children }) => (
  <h3 className="text-2xs font-semibold tracking-[0.12em] text-brand uppercase">{children}</h3>
);

const FooterColumn = ({ title, links }) => (
  <div>
    <ColumnTitle>{title}</ColumnTitle>
    <ul className="mt-4 space-y-2.5">
      {links.map((link) => (
        <li key={link.to}>
          <Link to={link.to} className={footerLink}>
            {link.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export default Footer;
